// lib/api/adminProjects.ts

import api from "../../../../lib/network/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  ApiResponse,
  AssignProjectPayload,
  CreateProjectPayload,
  PaginatedResponse,
  ProjectDetails,
  ProjectWithStats,
  ReviewApplicationPayload,
  UpdateProjectPayload,
  UpdateStatusPayload,
  UserAssignment,
} from "../types/adminProjects";

// ============ API FUNCTIONS ============

// Get all projects (admin)
export const getAdminProjects = async (params?: {
  search?: string;
  category?: string;
  isActive?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<PaginatedResponse<ProjectWithStats>>> => {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.append("search", params.search);
  if (params?.category) searchParams.append("category", params.category);
  if (params?.isActive) searchParams.append("isActive", params.isActive);
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/projects/admin${queryString ? `?${queryString}` : ""}`;

  const response =
    await api.get<ApiResponse<PaginatedResponse<ProjectWithStats>>>(url);
  return response;
};

// Get single project details (admin)
export const getAdminProjectDetails = async (
  projectId: string
): Promise<ApiResponse<ProjectDetails>> => {
  const response = await api.get<ApiResponse<ProjectDetails>>(
    `/projects/admin/${projectId}`
  );
  return response;
};

// Create project
export const createProject = async (
  payload: CreateProjectPayload
): Promise<ApiResponse<ProjectWithStats>> => {
  const response = await api.post<ApiResponse<ProjectWithStats>>(
    "/projects/admin",
    payload
  );
  return response;
};

// Update project
export const updateProject = async (
  projectId: string,
  payload: UpdateProjectPayload
): Promise<ApiResponse<ProjectWithStats>> => {
  const response = await api.patch<ApiResponse<ProjectWithStats>>(
    `/projects/admin/${projectId}`,
    payload
  );
  return response;
};

// Delete project
export const deleteProject = async (
  projectId: string
): Promise<ApiResponse<null>> => {
  const response = await api.delete<ApiResponse<null>>(
    `/projects/admin/${projectId}`
  );
  return response;
};

// Get project assignments
export const getProjectAssignments = async (
  projectId: string,
  params?: { status?: string; page?: number; limit?: number }
): Promise<ApiResponse<PaginatedResponse<UserAssignment>>> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append("status", params.status);
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/projects/admin/${projectId}/assignments${queryString ? `?${queryString}` : ""}`;

  const response =
    await api.get<ApiResponse<PaginatedResponse<UserAssignment>>>(url);
  return response;
};

// Assign project to user
export const assignProjectToUser = async (
  userId: string,
  payload: AssignProjectPayload
): Promise<ApiResponse<UserAssignment>> => {
  const response = await api.post<ApiResponse<UserAssignment>>(
    `/projects/admin/users/${userId}/assign`,
    payload
  );
  return response;
};

// Get user's assignments (admin view)
export const getUserAssignments = async (
  userId: string,
  params?: { status?: string; page?: number; limit?: number }
): Promise<ApiResponse<PaginatedResponse<UserAssignment>>> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append("status", params.status);
  if (params?.page) searchParams.append("page", params.page.toString());
  if (params?.limit) searchParams.append("limit", params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/projects/admin/users/${userId}/assignments${queryString ? `?${queryString}` : ""}`;

  const response =
    await api.get<ApiResponse<PaginatedResponse<UserAssignment>>>(url);
  return response;
};

// Unassign project
export const unassignProject = async (
  userProjectId: string,
  reason?: string
): Promise<ApiResponse<null>> => {
  const response = await api.delete<ApiResponse<null>>(
    `/projects/admin/assignments/${userProjectId}`,
    { data: { reason } }
  );
  return response;
};

// Review application
export const reviewApplication = async (
  userProjectId: string,
  payload: ReviewApplicationPayload
): Promise<ApiResponse<null>> => {
  const response = await api.post<ApiResponse<null>>(
    `/projects/admin/assignments/${userProjectId}/review`,
    payload
  );
  return response;
};

// Update user project status
export const updateUserProjectStatus = async (
  userProjectId: string,
  payload: UpdateStatusPayload
): Promise<ApiResponse<null>> => {
  const response = await api.patch<ApiResponse<null>>(
    `/projects/admin/assignments/${userProjectId}/status`,
    payload
  );
  return response;
};

// ============ HOOKS ============

export const useGetAdminProjects = (params?: {
  search?: string;
  category?: string;
  isActive?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ["adminProjects", params],
    queryFn: () => getAdminProjects(params),
    staleTime: 30000,
  });
};

export const useGetAdminProjectDetails = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ["adminProjectDetails", projectId],
    queryFn: () => getAdminProjectDetails(projectId!),
    enabled: !!projectId,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<ProjectWithStats>,
    ApiError,
    CreateProjectPayload
  >({
    mutationFn: (payload) => createProject(payload),
    onSuccess: (response) => {
      toast.success("Project Created", {
        description:
          response.message || "The project has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to create project. Please try again.";
      toast.error("Creation Failed", { description: errorMessage });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<ProjectWithStats>,
    ApiError,
    { projectId: string; payload: UpdateProjectPayload }
  >({
    mutationFn: ({ projectId, payload }) => updateProject(projectId, payload),
    onSuccess: (response) => {
      toast.success("Project Updated", {
        description:
          response.message || "The project has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
      queryClient.invalidateQueries({ queryKey: ["adminProjectDetails"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to update project. Please try again.";
      toast.error("Update Failed", { description: errorMessage });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<null>, ApiError, string>({
    mutationFn: (projectId) => deleteProject(projectId),
    onSuccess: (response) => {
      toast.success("Project Deleted", {
        description:
          response.message || "The project has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to delete project. Please try again.";
      toast.error("Delete Failed", { description: errorMessage });
    },
  });
};

export const useGetProjectAssignments = (
  projectId: string | undefined,
  params?: { status?: string; page?: number; limit?: number }
) => {
  return useQuery({
    queryKey: ["projectAssignments", projectId, params],
    queryFn: () => getProjectAssignments(projectId!, params),
    enabled: !!projectId,
  });
};

export const useAssignProject = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<UserAssignment>,
    ApiError,
    { userId: string; payload: AssignProjectPayload }
  >({
    mutationFn: ({ userId, payload }) => assignProjectToUser(userId, payload),
    onSuccess: (response) => {
      toast.success("Project Assigned", {
        description:
          response.message || "The project has been assigned successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["userAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["projectAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to assign project. Please try again.";
      toast.error("Assignment Failed", { description: errorMessage });
    },
  });
};

export const useGetUserAssignments = (
  userId: string | undefined,
  params?: { status?: string; page?: number; limit?: number }
) => {
  return useQuery({
    queryKey: ["userAssignments", userId, params],
    queryFn: () => getUserAssignments(userId!, params),
    enabled: !!userId,
  });
};

export const useUnassignProject = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<null>,
    ApiError,
    { userProjectId: string; reason?: string }
  >({
    mutationFn: ({ userProjectId, reason }) =>
      unassignProject(userProjectId, reason),
    onSuccess: (response) => {
      toast.success("Project Unassigned", {
        description:
          response.message || "The project has been unassigned successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["userAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["projectAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to unassign project. Please try again.";
      toast.error("Unassign Failed", { description: errorMessage });
    },
  });
};

export const useReviewApplication = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<null>,
    ApiError,
    { userProjectId: string; payload: ReviewApplicationPayload }
  >({
    mutationFn: ({ userProjectId, payload }) =>
      reviewApplication(userProjectId, payload),
    onSuccess: (response, variables) => {
      toast.success(
        variables.payload.action === "approve"
          ? "Application Approved"
          : "Application Rejected",
        { description: response.message }
      );
      queryClient.invalidateQueries({ queryKey: ["userAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["projectAssignments"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to review application. Please try again.";
      toast.error("Review Failed", { description: errorMessage });
    },
  });
};

export const useUpdateUserProjectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<null>,
    ApiError,
    { userProjectId: string; payload: UpdateStatusPayload }
  >({
    mutationFn: ({ userProjectId, payload }) =>
      updateUserProjectStatus(userProjectId, payload),
    onSuccess: (response) => {
      toast.success("Status Updated", { description: response.message });
      queryClient.invalidateQueries({ queryKey: ["userAssignments"] });
      queryClient.invalidateQueries({ queryKey: ["projectAssignments"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to update status. Please try again.";
      toast.error("Update Failed", { description: errorMessage });
    },
  });
};
